import { formatLocation } from "@/utils/formatLocation";

/**
 * Builds the confirmation e-mail an admin copies into their own mail client.
 * Nothing is sent from the app, so the result is plain text meant to be edited
 * by hand before sending.
 *
 * Only customer-facing unit prices are quoted: the retail (net) price and the
 * outgoing gross price per person per night. No subtotals or grand total are
 * written out, and the incoming price the accommodation charges us must never
 * appear here.
 */
export const buildBookingConfirmationEmail = (
  booking: any
): { subject: string; body: string } => ({
  subject: `Foglalás visszaigazolása – ${formatPublicReference(
    booking.publicReference
  )}`,
  body: buildBody(booking),
});

export const formatPublicReference = (reference?: string): string => {
  if (!reference) return "";
  return reference.match(/.{1,4}/g)!.join("-");
};

/**
 * Booking item dates are date-only strings, so they are parsed at local midnight:
 * parsing them as UTC would shift the rendered day for viewers behind UTC.
 * date-fns is deliberately not used here — importing it makes this module
 * unloadable in the Mocha test harness.
 */
const parseDateOnly = (date: string) => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date?: string) => {
  if (!date) return "-";
  return parseDateOnly(date).toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatPrice = (value?: number | null) =>
  value == null ? "-" : `${value.toLocaleString("hu-HU")} Ft`;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Rounded because a DST switch between the two dates makes the span 23 or 25 hours.
const nightsOf = (item: any) =>
  Math.round(
    (parseDateOnly(item.endDate).getTime() -
      parseDateOnly(item.startDate).getTime()) /
      MS_PER_DAY
  );

const buildBody = (booking: any) => {
  const items: any[] = booking.items ?? [];
  const lines: string[] = [];

  lines.push(`Kedves ${booking.contactName || "Partnerünk"}!`, "");
  lines.push(
    "Köszönjük a megrendelést! Az alábbiakban visszaigazoljuk a foglalás részleteit.",
    ""
  );
  lines.push(
    `Foglalási azonosító: ${formatPublicReference(booking.publicReference)}`
  );
  if (booking.companyName) lines.push(`Cég: ${booking.companyName}`);
  if (booking.costCenterName) {
    lines.push(`Költséghely: ${booking.costCenterName}`);
  }
  lines.push("", "A foglalás elemei:", "");

  items.forEach((item, index) => {
    const nights = nightsOf(item);
    const location = formatLocation(item.city, item.address);
    // Items booked before the gross column existed carry no gross price, so the
    // gross rate is only written out when the item has one.
    const gross = item.outgoingGrossPerPersonPerNight;

    lines.push(
      `${index + 1}. ${item.accommodation}${location ? ` (${location})` : ""}`
    );
    lines.push(`   Szolgáltatás: ${item.product}`);
    lines.push(`   Érkezés: ${formatDate(item.startDate)}`);
    lines.push(`   Távozás: ${formatDate(item.endDate)}`);
    lines.push(`   Éjszakák száma: ${nights}`);
    lines.push(`   Létszám: ${item.numberOfPeople} fő`);
    lines.push(
      `   Ár: ${formatPrice(item.retailPrice)} + áfa / fő / éj` +
        (gross == null ? "" : ` (bruttó ${formatPrice(gross)} / fő / éj)`)
    );
    // ifaCount is filled in after the stay, so only the per-guest-night rate is quoted.
    if (item.ifaAmount) {
      lines.push(
        `   Idegenforgalmi adó: ${formatPrice(item.ifaAmount)} / fő / éj`
      );
    }
    lines.push("");
  });

  if (booking.cancellationPolicy) {
    lines.push("Lemondási feltételek:", booking.cancellationPolicy, "");
  }

  lines.push(
    "Kérjük, ellenőrizze az adatokat, és eltérés esetén válaszoljon erre a levélre.",
    ""
  );
  lines.push("Üdvözlettel,");

  return lines.join("\n");
};
